import React, { useEffect, useRef } from 'react';
import { Box, Typography, Container, useMediaQuery } from '@mui/material';
import { gsap } from 'gsap';

import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { gradientline } from '../../../../../assets';
import { HeroSection, ProjectDetails } from '../ProjectInterface';

interface BannerProps {
  herosection: HeroSection;
  projectdetails: ProjectDetails;
}

const CDN_URL =
  process.env.NEXT_PUBLIC_STORAGE_DN_URL || 'https://dprstorage.b-cdn.net';

const resolveImageUrl = (path: string) => {
  if (!path) return '';
  return path.startsWith('http') ? path : `${CDN_URL}${path}`;
};

const Banner: React.FC<BannerProps> = ({ herosection, projectdetails }) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const isMobile = useMediaQuery('(max-width: 600px)');

  useEffect(() => {
    gsap.fromTo(
      textRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1.2, ease: 'power2.out' },
    );
    gsap.fromTo(
      imageRef.current,
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 1.5, ease: 'power2.out', delay: 0.5 },
    );
  }, []);

  // Determine alignment based on herosection.alig
  const alignRight = herosection.alig === 'right';

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: isMobile ? 'auto' : '100vh',
        backgroundImage: `url(${resolveImageUrl(herosection.backgroundimage)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        py: { xs: '100px', md: 0 },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.45)', // lighter overlay for better contrast
          zIndex: 1,
        },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          display: 'flex',
          flexDirection: isMobile
            ? 'column'
            : alignRight
              ? 'row-reverse'
              : 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'center',
          gap: { xs: 2, md: 0 },
        }}
      >
        {/* Left Side Content */}
        <Box
          sx={{
            width: isMobile ? '100%' : '45%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 2,
            position: 'relative',
            zIndex: 3,
            // Align left or right based on alignRight
            ml: isMobile ? 0 : alignRight ? 'auto' : 0,
            mr: isMobile ? 0 : alignRight ? 0 : 'auto',
            textAlign: alignRight && !isMobile ? 'right' : 'left',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 0,
            }}
          >
            <Box
              component="img"
              src={resolveImageUrl(herosection.projectlogo)}
              alt="Logo"
              sx={{
                height: isMobile ? '70px' : '30px',
                // mb: 2,
                mt: 6,
              }}
            />
          </Box>
          {isMobile ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 3,
              }}
            >
              {/* <img
                src={gradientline}
                alt="Decorative gradient divider"
                loading="lazy"
                decoding="async"
                style={{
                  position: 'absolute',
                  left: '-100%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '580px',
                  height: 'auto',
                }}
              /> */}
            </Box>
          ) : (
            ''
          )}

          {/* Luxury Text */}
          <Box ref={textRef} sx={{ color: 'lightgrey' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: '580',
                fontSize: { xs: '20px', md: '28px' },
                color: 'white',
                // display: "inline-block",
                textAlign: alignRight && !isMobile ? 'center' : 'center',
                // textAlign: "center",
                fontFamily: 'Futura,  sans-serif !important',
            letterSpacing:"2px"
              }}
            >
              {herosection.h1}
            </Typography>
            {/* <Typography
              variant="h5"
              sx={{
                fontWeight: "580",
                fontSize: { xs: "20px", md: "28px" },
                color:'white',
                display: "inline-block",
              }}
            >
              {herosection.h2}
            </Typography> */}

            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 3,
                  textAlign: 'center',
                  width: '100%',
                  gap: { xs: 2, md: 4 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    color: 'white',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: 'white',
                      fontSize: { xs: '24px', md: '30px' },
                      fontWeight: 500,
                    }}
                  >
                    {projectdetails.bhk?.join(', ')} BHK
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      mb: 1,
                      color: 'white',
                      fontWeight: 400,
                      opacity: 0.75,
                      mt: 0.5,
                    }}
                  >
                    {herosection.h3}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      height: '55px',
                      width: isMobile ? '1.5px' : '2px',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'white',
                      borderRadius: '5px',
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    // alignItems: "flex-start",
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: '24px', md: '30px' },
                      fontWeight: 500,
                      color: 'white',
                    }}
                  >
                    {projectdetails.sqft
                      ?.map((s) => s.toLocaleString())
                      .join(' - ')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      mb: 1,
                      color: 'white',
                      fontWeight: 400,
                      textAlign: 'center',
                      opacity: 0.75,
                      mt: 0.5,
                    }}
                  >
                    SQ. FT
                  </Typography>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                my: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'left',
                width: '100%',
                position: 'relative',
              }}
            >
              {isMobile ? (
                ''
              ) : (
                // <img src={gradientline} alt="Gradient Line" style={{ width: "100%", position: 'absolute', top: '50%', left: '0%', transform: 'translate(-13%, -50%)' }} />
                <img
                  src={gradientline}
                  alt="Decorative gradient divider"
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: '100%',
                    position: 'absolute',
                    top: '50%',
                    left: '0%',
                    transform: alignRight
                      ? 'translate(6%, -50%)' // For right alignment
                      : 'translate(-13%, -50%)', // Default
                  }}
                />
              )}
            </Box>

            {!isMobile && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <LocationOnOutlinedIcon
                    sx={{ color: 'lightgrey', fontSize: 30, fontWeight: 700 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ color: '#fff', fontWeight: 400, letterSpacing: 1 }}
                  >
                    {herosection.loc}
                  </Typography>
                </Box>
                {!projectdetails.title?.toLowerCase().includes('vibrant') && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '12px',
                        opacity: 0.8,
                        fontWeight: 400,
                        letterSpacing: 1,
                        color: '#fff',
                      }}
                    >
                      RERA: {herosection.rera || 'N/A'}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Right Side - Apartment Image */}
        {!isMobile ? (
          <Box
            ref={imageRef}
            sx={{
              position: 'absolute',
              bottom: -7,
              right: 0,
              width: isMobile ? '100%' : '100%',
              height: '100%',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={resolveImageUrl(herosection.projectimage)}
              srcSet={`${resolveImageUrl(
                herosection.projectimage,
              )}?w=480 480w, ${resolveImageUrl(
                herosection.projectimage,
              )}?w=768 768w, ${resolveImageUrl(
                herosection.projectimage,
              )}?w=1200 1200w`}
              sizes="(max-width: 600px) 100vw, 100vw"
              alt={herosection.h1 || 'Project hero image'}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              style={{ width: '100%', height: '100%', borderRadius: 'inherit' }}
            />
          </Box>
        ) : (
          <>
            <Box
              ref={imageRef}
              sx={{
                width: '150%',
                height: 'auto',
                
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: '20px',
                            transform: 'translateX(-20%)', // shift image 20% left
                // transition: 'transform 0.3s ease',
              }}
            >
              <img
                src={resolveImageUrl(herosection.projectimage)}
                srcSet={`${resolveImageUrl(
                  herosection.projectimage,
                )}?w=360 360w, ${resolveImageUrl(
                  herosection.projectimage,
                )}?w=640 640w, ${resolveImageUrl(
                  herosection.projectimage,
                )}?w=960 960w`}
                sizes="(max-width: 600px) 100vw, 65vw"
                alt={herosection.h1 || 'Project hero image'}
                loading="lazy"
                decoding="async"
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 'inherit',
                }}
              />
            </Box>
          </>
        )}

        {isMobile && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                mt: 3,
                textAlign: 'center',
                width: '100%',
                gap: { xs: 4, md: 6 },
                position: 'relative',
                zIndex: 3,
              }}
            >
              {/* LEFT SECTION */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minWidth: '130px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    height: '48px', // same as right section height to maintain alignment
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: '20px', md: '24px' },
                      fontWeight: 600,
                      color: 'white',
                      lineHeight: 1.1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {projectdetails.bhk?.join(', ')} BHK
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    opacity: 0.75,
                    mt: 1,
                    color: 'white',
                    textAlign: 'center',
                    lineHeight: 1.3,
                  }}
                >
                  {herosection.h3}
                </Typography>
              </Box>

              {/* DIVIDER */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    height: '60px',
                    width: '1.5px',
                    background: 'white',
                    borderRadius: '5px',
                    opacity: 0.8,
                  }}
                />
              </Box>

              {/* RIGHT SECTION */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minWidth: '130px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    height: '48px', // matches left side
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: '20px', md: '24px' },
                      fontWeight: 600,
                      color: 'white',
                      lineHeight: 1.1,
                      textAlign: 'center',
                    }}
                  >
                    {projectdetails.sqft
                      ?.map((s) => s.toLocaleString())
                      .join(' - ')}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    textAlign: 'center',
                    opacity: 0.75,
                    mt: 1,
                    color: 'white',
                    lineHeight: 1.3,
                  }}
                >
                  SQ. FT
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 3,
              }}
            >
              <img
                src={gradientline}
                alt="Decorative gradient divider"
                loading="lazy"
                decoding="async"
                style={{
                  position: 'absolute',
                  // left: '-100%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '580px',
                  height: 'auto',
                }}
              />
            </Box>

            {/* LOCATION */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                gap: 1,
                mt: 2,
                position: 'relative',
                zIndex: 3,
              }}
            >
              <LocationOnOutlinedIcon sx={{ color: 'white', fontSize: 30 }} />
              <Typography
                variant="body1"
                sx={{
                  fontSize: '16px',
                  fontWeight: 400,
                  letterSpacing: 1,
                  color: 'white',
                }}
              >
                {herosection.loc}
              </Typography>
            </Box>

            {/* RERA */}
            {!projectdetails.title?.toLowerCase().includes('vibrant') && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 2,
                  position: 'relative',
                  zIndex: 3,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                    letterSpacing: 1,
                    color: 'white',
                  }}
                >
                  RERA NO : {herosection.rera}
                </Typography>
              </Box>
            )}
          </>
        )}

      </Container>
    </Box>
  );
};

export default Banner;
